import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Badge from "../../components/common/Badge/Badge";
import TabsNavigation from "../../components/common/TabsNavigation/TabsNavigation";
import Table from "../../components/common/Table/Table";
import ValidacionesHistorico from "../../components/common/ValidacionesHistorico/ValidacionesHistorico";
import "./FichaObraMenor.scss";
import DetalleLayout from "../../Layouts/DetalleLayout/DetalleLayout";
import { getApiInstance } from "../../utils/api";
import { CiCircleCheck, CiClock1 } from "react-icons/ci";
import { CgDanger } from "react-icons/cg";
import { addOrUpdateRecentItem, formatDate } from "../../utils/utils";

// Elementos del formulario para documentos
const baseFormElementsDocumentos = [
    {
        elements: [
            {
                name: "obs",
                type: "textarea",
                label: "Descripción",
                placeholder: "Ingrese una descripción del documento",
                required: true,
                disabled: false,
                labelVisible: true,
                visible: true,
            },
            {
                name: "fichero",
                type: "document",
                label: "Documento",
                placeholder: "Subir documento",
                required: true,
                disabled: false,
                labelVisible: true,
                visible: true,
            },
        ],
    },
];

export default function FichaObraMenor({ user }) {
    const { id } = useParams();
    const [obraMenor, setObraMenor] = useState(null);
    const [documentos, setDocumentos] = useState([]);
    const [activeTabGeneral, setActiveTabGeneral] = useState("informacion");
    const [activeTabDatos, setActiveTabDatos] = useState("documentacion");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isHistoricoOpen, setIsHistoricoOpen] = useState(false);
    const [documentoHistorico, setDocumentoHistorico] = useState(null);

    /* Mismas reglas que la aplicación antigua (Views/ObrasMenores/Details.cshtml):
       sobre una obra cerrada (MinorCenterState.CLOSED = 5) no se toca nada, validar
       es de administradores (perfiles 1 y 2) y el histórico lo ven además los
       intermediarios (perfil 4). */
    const puedeGestionar =
        Number(user?.codAcceso) === 1 && obraMenor?.codEstado !== 5;
    const puedeValidar =
        puedeGestionar &&
        (Number(user?.role) === 1 || Number(user?.role) === 2);
    const puedeVerValidaciones = [1, 2, 4].includes(Number(user?.role));

    const abrirHistorico = (row) => {
        setDocumentoHistorico(row);
        setIsHistoricoOpen(true);
    };

    useEffect(() => {
        if (obraMenor != null) {
            addOrUpdateRecentItem({
                id: obraMenor.codObraMenor,
                title: obraMenor.nombre,
                type: "obrasMenores",
            });
        }
    }, [obraMenor]);

    const fetchObraMenor = async (id) => {
        try {
            const api = getApiInstance();
            const response = await api.get(`/api/ObraMenor/GetObraMenor/${id}`);
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const fetchDocumentosObraMenor = async (id) => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/ObraMenorDocumento/GetDocumentosFromObraMenor/${id}`
            );
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    useEffect(() => {
        const loadObraMenorData = async () => {
            try {
                setLoading(true);
                setError(null);

                const results = await Promise.allSettled([
                    fetchObraMenor(id),
                    fetchDocumentosObraMenor(id),
                ]);

                if (results[0].status === "fulfilled") {
                    setObraMenor(results[0].value);
                } else {
                    console.error(
                        "Error cargando la obra menor:",
                        results[0].reason
                    );
                    setError(
                        "No se pudo cargar la información principal de la obra menor."
                    );
                }

                if (results[1].status === "fulfilled") {
                    setDocumentos(results[1].value);
                } else {
                    console.warn(
                        "No se encontraron documentos para la obra:",
                        results[1].reason
                    );
                }
            } catch (err) {
                console.error("Error cargando datos de la obra menor:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            loadObraMenorData();
        }
    }, [id]);

    const getEstadoBadge = (estado) => {
        const estadoConfig = {
            VALIDADO: { type: "success", label: "Validado" },
            PENDIENTE: { type: "info", label: "Pendiente" },
            INCIDENCIA: { type: "warning", label: "Incidencia" },
            VENCIDO: { type: "error", label: "Vencido" },
            ACTIVA: { type: "success", label: "Activa" },
        };
        return estadoConfig[estado] || estadoConfig["PENDIENTE"];
    };

    const tabsGeneral = [
        { id: "informacion", label: "Información General" },
        { id: "localizacion", label: "Localización" },
    ];

    const tabsDatos = [{ id: "documentacion", label: "Documentación" }];

    const formElementsDocumentos = baseFormElementsDocumentos.map(
        (grupo) => ({
            ...grupo,
            elements: [
                ...grupo.elements,
                {
                    name: "codObraMenor",
                    type: "number",
                    label: "Obra menor",
                    visible: false,
                    defaultValue: Number(id),
                },
            ],
        })
    );

    const renderInformacionGeneral = () => (
        <div className="ficha-gestor__info">
            <div className="ficha-gestor__field">
                <label>Productor:</label>
                <span>{obraMenor.productor}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Código de obra:</label>
                <span>{obraMenor.nombre}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Fecha inicio:</label>
                <span>{formatDate(obraMenor.fechaInicio)}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Fecha fin:</label>
                <span>{formatDate(obraMenor.fechaFin)}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Empresa intermediaria:</label>
                <span>{obraMenor.intermediario}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Cadena:</label>
                <span>{obraMenor.cadena}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Cantidad generada RP (Kg):</label>
                <span>{obraMenor.cantidadRP}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Cantidad generada RNP (Tn):</label>
                <span>{obraMenor.cantidadRNP}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Observaciones:</label>
                <span>{obraMenor.obs}</span>
            </div>
        </div>
    );

    const renderLocalizacion = () => (
        <div className="ficha-gestor__info">
            <div className="ficha-gestor__field">
                <label>Dirección:</label>
                <span>{obraMenor.direccion}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Localidad:</label>
                <span>{obraMenor.localidad}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Provincia:</label>
                <span>{obraMenor.provincia}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Comunidad:</label>
                <span>{obraMenor.comunidad}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>País:</label>
                <span>{obraMenor.pais}</span>
            </div>
        </div>
    );

    const renderGeneralContent = () => {
        switch (activeTabGeneral) {
            case "informacion":
                return renderInformacionGeneral();
            case "localizacion":
                return renderLocalizacion();
            default:
                return renderInformacionGeneral();
        }
    };

    const renderDatosContent = () => {
        switch (activeTabDatos) {
            case "documentacion":
                return (
                    <div className="ficha-centro__documentacion">
                        <Table
                            columns={[
                                {
                                    key: "documento",
                                    label: "Documento",
                                    visible: true,
                                    sortable: true,
                                    documentProp: "ficheroBase64",
                                    type: "document",
                                },
                                {
                                    key: "obs",
                                    label: "Descripción",
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "codEstado",
                                    label: "Validación",
                                    visible: true,
                                    sortable: true,
                                    titleProp: "lastValidationNote",
                                },
                            ]}
                            dataTable={documentos}
                            noResultsMessage="No hay documentos disponibles"
                            rowsPerPage={10}
                            showCheckboxes={false}
                            showAdvancedFilters={false}
                            /* Sobre una obra menor cerrada no se suben documentos,
                               como en la aplicacion antigua. */
                            hideAddButton={!puedeGestionar}
                            codTipoValidacion={9}
                            actions={[
                                ...(puedeValidar
                                    ? [
                                          {
                                              label: "Validar",
                                              actionType: "validate",
                                              icon: <CiCircleCheck size={16} />,
                                          },
                                      ]
                                    : []),
                                ...(puedeVerValidaciones
                                    ? [
                                          {
                                              label: "Ver validaciones",
                                              action: abrirHistorico,
                                              icon: <CiClock1 size={16} />,
                                          },
                                      ]
                                    : []),
                                ...(puedeGestionar ? ["Eliminar"] : []),
                            ]}
                            elementName="Documento"
                            keyProp="codDocumento"
                            sortProp="documento"
                            searchTerms={["documento", "obs"]}
                            stateProp="codEstado"
                            states={[
                                {
                                    label: "Sin datos",
                                    type: "info",
                                    Icon: CiClock1,
                                },
                                {
                                    label: "Pendiente de validar",
                                    type: "info",
                                    Icon: CiClock1,
                                },
                                {
                                    label: "Validación con incidencias",
                                    type: "danger",
                                    Icon: CgDanger,
                                },
                                {
                                    label: "Validada",
                                    type: "success",
                                    Icon: CiCircleCheck,
                                },
                            ]}
                            stateChangeOptions={[
                                {
                                    value: "1",
                                    label: "Pendiente de validar",
                                },
                                {
                                    value: "2",
                                    label: "Validación con incidencias",
                                },
                                { value: "3", label: "Validada" },
                            ]}
                            /* Sin permiso para validar no se pasa la URL: así la tabla no
                               ofrece el cambio de estado ni el botón de validar en bloque. */
                            stateChangeUrl={
                                puedeValidar
                                    ? "/api/Validaciones/SetValidacion"
                                    : undefined
                            }
                            formElements={formElementsDocumentos}
                            addApiUrl="/api/ObraMenorDocumento/SetDocumento"
                            deleteApiUrl="/api/ObraMenorDocumento/DeleteDocumento"
                            successMessages={{
                                add: "Documento añadido correctamente",
                                edit: "Documento editado correctamente",
                                delete: "Documento eliminado correctamente",
                            }}
                            onDataUpdate={async () => {
                                const data =
                                    await fetchDocumentosObraMenor(id);
                                setDocumentos(data);
                            }}
                            loading={loading}
                            userCodAcceso={Number(user.codAcceso)}
                            userCodPerfil={Number(user.role)}
                            /* El intermediario sube y borra documentos, como en
                               la antigua (ObrasMenores/Details.cshtml). */
                            intermediarioPuedeEscribir
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <DetalleLayout
            title={
                obraMenor
                    ? `Obra menor ${obraMenor.nombre} - ${obraMenor.direccion} ${obraMenor.localidad}`
                    : "Cargando..."
            }
            backLink="/obras-menores"
            backText="Volver a Obras menores"
            loading={loading}
            error={error}
            notFound={!obraMenor && !loading && !error}
            className="ficha-gestor"
        >
            {!loading && !error && obraMenor && (
                <div className="ficha-gestor__content">
                    <div className="ficha-gestor__section">
                        <div className="ficha-gestor__section-header">
                            <h2>Información General</h2>
                        </div>

                        <TabsNavigation
                            tabs={tabsGeneral}
                            activeTab={activeTabGeneral}
                            onTabChange={setActiveTabGeneral}
                        />

                        <div className="ficha-gestor__section-content">
                            {renderGeneralContent()}
                        </div>
                    </div>

                    <div className="ficha-gestor__section">
                        <div className="ficha-gestor__section-header">
                            <h2>Datos de la obra menor</h2>
                        </div>

                        <TabsNavigation
                            tabs={tabsDatos}
                            activeTab={activeTabDatos}
                            onTabChange={setActiveTabDatos}
                        />

                        <div className="ficha-gestor__section-content">
                            {renderDatosContent()}
                        </div>
                    </div>
                </div>
            )}

            {/* 9 = ObrasMenoresDocumentos en TipoValidacionTable. */}
            <ValidacionesHistorico
                isOpen={isHistoricoOpen}
                onClose={() => setIsHistoricoOpen(false)}
                codTipoValidacion={9}
                codClave={documentoHistorico?.codDocumento}
            />
        </DetalleLayout>
    );
}
